import { supabase } from './supabase';

export interface Notification {
  id: string;
  recipient_id: string;
  recipient_role: string | null;
  type: string;
  title: string;
  body: string;
  project_id: string | null;
  link: string | null;
  push_verstuurd: boolean;
  email_verstuurd: boolean;
  gelezen: boolean;
  gelezen_op: string | null;
  priority: 'normal' | 'critical';
  created_at: string;
  projects?: {
    project_number: string;
    name: string;
  } | null;
}

export async function fetchNotifications(limit = 20, onlyUnread = false) {
  let query = supabase
    .from('notifications')
    .select(
      `
      *,
      projects:project_id (
        project_number,
        name
      )
    `,
    )
    .order('created_at', { ascending: false })
    .limit(limit);

  if (onlyUnread) {
    query = query.eq('gelezen', false);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as Notification[];
}

export async function fetchUnreadCount(): Promise<number> {
  const { count, error } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('gelezen', false);

  if (error) throw error;
  return count || 0;
}

/** Ongelezen kritieke notificaties (voor de blokkerende CriticalAlertsGate-pop-up). */
export async function fetchUnreadCriticalNotifications(): Promise<Notification[]> {
  const { data, error } = await supabase
    .from('notifications')
    .select('*')
    .eq('gelezen', false)
    .eq('priority', 'critical')
    .order('created_at', { ascending: false })
    .limit(20);

  if (error) throw error;
  return (data || []) as Notification[];
}

export async function markAsRead(notificationId: string) {
  const { error } = await supabase
    .from('notifications')
    .update({
      gelezen: true,
      gelezen_op: new Date().toISOString(),
    })
    .eq('id', notificationId);

  if (error) throw error;
}

export async function markAllAsRead() {
  const { error } = await supabase
    .from('notifications')
    .update({
      gelezen: true,
      gelezen_op: new Date().toISOString(),
    })
    .eq('gelezen', false);

  if (error) throw error;
}

export async function createNotificationForEvent(
  eventType: string,
  title: string,
  body: string,
  projectId?: string,
  link?: string,
  priority: 'normal' | 'critical' = 'normal',
): Promise<number> {
  const { data, error } = await supabase.rpc('create_notifications_for_event', {
    p_event_type: eventType,
    p_title: title,
    p_body: body,
    p_project_id: projectId || null,
    p_link: link || null,
    p_priority: priority,
  });

  if (error) throw error;
  return data as number;
}

export function getNotificationIcon(type: string): string {
  const iconMap: Record<string, string> = {
    quote_sent_to_client: 'FileText',
    quote_signed_by_client: 'CheckCircle',
    quote_rejected_by_client: 'XCircle',
    quote_unsigned_reminder: 'Clock',
    oa_contract_sent: 'Send',
    oa_contract_signed: 'CheckCircle',
    oa_contract_rejected: 'XCircle',
    oa_contract_unsigned_reminder: 'Clock',
    werkvoorbereiding_stap_open: 'AlertTriangle',
    verf_bestelling_verstuurd: 'Paintbrush',
    steiger_opdracht_verstuurd: 'Construction',
    project_startklaar: 'Rocket',
    project_gestart: 'Play',
    geen_daglog_vandaag: 'AlertTriangle',
    houtrot_gemeld: 'AlertTriangle',
    meerwerk_signed_by_client: 'CheckCircle',
    meerwerk_rejected_by_client: 'XCircle',
    meerwerk_unsigned_reminder: 'Clock',
    timmerwerk_klaar: 'Hammer',
    planning_verschuiving: 'Calendar',
    oa_voortgang_stilte: 'AlertTriangle',
    verwachte_regen: 'CloudRain',
    opleverpunten_geplaatst: 'MapPin',
    herstelfotos_ingediend: 'Camera',
    punten_goedgekeurd: 'CheckCircle',
    punten_afgekeurd: 'XCircle',
    eindoplevering_vrijgegeven_oa: 'Unlock',
    eindoplevering_getekend: 'Award',
    steiger_afgemeld: 'Construction',
    herstelpunten_niet_opgepakt: 'AlertTriangle',
    verkoopfactuur_verstuurd: 'Receipt',
    factuur_verlopen: 'AlertTriangle',
    betaling_ontvangen: 'DollarSign',
    termijnbon_verstuurd: 'FileText',
    inkoopfactuur_ontvangen: 'Download',
    inkoopfactuur_verlopen: 'AlertTriangle',
    alle_checks_groen: 'CheckCircle',
    project_afgesloten: 'Archive',
    review_niet_ingevuld: 'Clock',
    hoogwerker_datum_afstemmen: 'Calendar',
    vergunning_verloopt: 'AlertTriangle',
    raamovereenkomst_verloopt: 'AlertTriangle',
    project_created: 'FolderPlus',
    project_status_changed: 'RefreshCw',
    contract_signed: 'CheckCircle',
    invoice_created: 'FileText',
    payment_received: 'DollarSign',
    purchase_invoice_received: 'Download',
    task_assigned: 'UserPlus',
    delivery_scheduled: 'Calendar',
    document_uploaded: 'Upload',
    meerwerk_created: 'PlusCircle',
    comment_added: 'MessageSquare',
    mention_in_communication: 'AtSign',
    inkoopfactuur_goedgekeurd: 'CheckCircle',
    voortgang_update: 'TrendingUp',
    taak_voltooid: 'CheckSquare',
    nieuw_bericht_inbox: 'Mail',
    gebruiker_uitgenodigd: 'UserPlus',
    nieuw_bericht_communicatie: 'MessageSquare',
    nieuw_bericht_oa_communicatie: 'HardHat',
    lead_created: 'UserPlus',
    lead_assigned: 'UserCheck',
    afspraak_herinnering: 'Clock',
    beschikbaarheid_herinnering: 'Clock',
    opname_ingepland: 'ClipboardCheck',
    offerte_verlopen: 'AlertTriangle',
    cluster_verstuurd: 'Send',
    cluster_ondertekend: 'CheckCircle',
    credit_factuur_aangemaakt: 'FileText',
    project_budget_overschreden: 'AlertTriangle',
    offerte_vraag: 'MessageSquare',
    meerwerk_vraag: 'MessageSquare',
    contract_opmerking: 'MessageSquare',
    cluster_opmerking: 'MessageSquare',
    rolsteiger_levering_aangevraagd: 'Truck',
    rolsteiger_levering_ingepland: 'CalendarCheck',
    rolsteiger_onderweg: 'Truck',
    rolsteiger_geleverd: 'PackageCheck',
    rolsteiger_afgemeld: 'Construction',
    rolsteiger_opgehaald: 'PackageCheck',
    ongematchte_transacties: 'AlertTriangle',
    bank_sync_stil: 'AlertTriangle',
    iban_gewijzigd: 'AlertTriangle',
    graph_api_health_warning: 'AlertTriangle',
  };
  return iconMap[type] || 'Bell';
}

export function getNotificationColor(type: string): string {
  if (
    type === 'iban_gewijzigd' ||
    type === 'ongematchte_transacties' ||
    type === 'bank_sync_stil' ||
    type === 'graph_api_health_warning'
  ) {
    return 'text-red-600 bg-red-50';
  }
  if (
    type.includes('signed') ||
    type.includes('goedgekeurd') ||
    type.includes('groen') ||
    type.includes('betaling') ||
    type.includes('klaar')
  ) {
    return 'text-green-600 bg-green-50';
  }
  if (type.includes('rejected') || type.includes('afgekeurd') || type.includes('afgewezen')) {
    return 'text-red-600 bg-red-50';
  }
  if (
    type.includes('reminder') ||
    type.includes('verlopen') ||
    type.includes('stilte') ||
    type.includes('niet_opgepakt') ||
    type.includes('houtrot') ||
    type.includes('regen') ||
    type.includes('budget_overschreden') ||
    type.includes('herinnering')
  ) {
    return 'text-amber-600 bg-amber-50';
  }
  if (type.includes('vraag') || type.includes('opmerking')) {
    return 'text-orange-600 bg-orange-50';
  }
  return 'text-blue-600 bg-blue-50';
}

export function formatTimeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'zojuist';
  if (diffMin < 60) return `${diffMin}m geleden`;
  if (diffHour < 24) return `${diffHour}u geleden`;
  if (diffDay < 7) return `${diffDay}d geleden`;
  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}
